/* eslint-disable import/no-cycle */
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  ElementPartTypeEnum,
  StepperSignEnum,
  TemplateStructureEnum,
} from '@/types';
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
import mediaDisplayFieldConfig from '@/components/template-builder/config/fields/media-display-config';
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
import getGameSlotSymbolsEditorFieldConfig from '@/components/template-builder/config/fields/game-slot-symbols-editor-config';
import getGameSlotCombinationsEditorFieldConfig from '@/components/template-builder/config/fields/game-slot-combinations-editor-config';
import getGameTestButtonFieldConfig from '@/components/template-builder/config/fields/game-test-button-config';
import getGameStockEditorFieldConfig from '@/components/template-builder/config/fields/game-stock-editor-config';
import {
  setCssProperty,
  setMediaDisplay,
  setAttribute,
  setGameSlotMachineAttribute,
  setGameCssProperty,
} from '@/components/template-builder/setters';
import {
  getClassByName,
  getCssPropertyByName,
  getAttributeByName,
  getAlwaysTrue,
  getGameCssProperty,
} from '@/components/template-builder/getters';
import getConditionsAndRepetitionGroup from '@/components/template-builder/config/fields/conditions-repetition-group-helper';
// eslint-disable-next-line import/no-cycle
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';

const displayWidgetGameSlotMachineConfigV4: WidgetTypeConfig = {
  tabPanel: {
    label: 'templateBuilder.widgetsHeaders.gameSlotMachine',
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.gameConfiguration',
        scrollable: true,
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameSlotSymbols',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.gameSlotSymbols',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-symbols',
                    getter: getAttributeByName,
                    setters: [setGameSlotMachineAttribute],
                  }],
                  ...getGameSlotSymbolsEditorFieldConfig(),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameSlotCombinations',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.gameSlotWinCombinations',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-win-combinations',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getGameSlotCombinationsEditorFieldConfig(),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameSettings',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.gameSpinDuration',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-spin-duration',
                    getter: getAttributeByName,
                    setters: [setGameSlotMachineAttribute],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.NONE, 500, 1000, 5000),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.NONE, 500, 1000, 5000).options,
                    defaultValue: 2000,
                    suffix: 'ms',
                  },
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameStock',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: '',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-win-combinations',
                    getter: getAttributeByName,
                    setters: [setAttribute],
                  }],
                  ...getGameStockEditorFieldConfig({ gameType: 'slotMachine' }),
                },
              ],
            },
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameTest',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.testGame',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'test',
                    getter: getAlwaysTrue,
                    setters: [],
                  }],
                  ...getGameTestButtonFieldConfig('slotMachine'),
                },
              ],
            },
          ],
        },
      },
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.gameSlotMachineStyles',
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.gameSlotMachineAppearance',
              selector: 'body #spm_body #{ID}',
              canSave: true,
              elementPartType: ElementPartTypeEnum.STYLES,
              items: [
                {
                  label: 'templateBuilder.fields.gameSlotSymbolSize',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-symbol-size',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 40, 150),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 40, 150).options,
                    defaultValue: 80,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameSlotReelHeight',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-reel-height',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 60, 200),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 60, 200).options,
                    defaultValue: 100,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameSlotFrameColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-frame-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#1a1a1a', true, false, true),
                },
                {
                  label: 'templateBuilder.fields.gameSlotBackgroundColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-background-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#ffffff', true, false, true),
                },
                {
                  label: 'templateBuilder.fields.gameSlotLineColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-line-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#ffd700', true, false),
                },
                {
                  label: 'templateBuilder.fields.gameSlotLineWidth',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-line-width',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 10),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 10).options,
                    defaultValue: 3,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameSlotFrameRadius',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-frame-radius',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 30),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 30).options,
                    defaultValue: 16,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameSlotReelRadius',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-reel-radius',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 20),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 20).options,
                    defaultValue: 4,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameSlotReelGap',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-reel-gap',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 20),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 20).options,
                    defaultValue: 8,
                  },
                },
                {
                  label: 'templateBuilder.fields.gameSlotReelsPadding',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-reels-padding',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 30),
                  options: {
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 30).options,
                    defaultValue: 12,
                  },
                  displayToggle: false,
                },
                {
                  label: 'templateBuilder.fields.gameSlotTextColor',
                  selector: '{SELECTOR}',
                  properties: [{
                    name: 'data-text-color',
                    getter: getGameCssProperty,
                    setters: [setGameCssProperty],
                  }],
                  ...getColorFieldConfig('#333333', true, false),
                },
              ],
            },
            getWidgetCommonStylesGroup(),
          ],
        },
      },
      getConditionsAndRepetitionGroup('body #spm_body #{ID}'),
    ],
  },
};

export default displayWidgetGameSlotMachineConfigV4;
